"use client"

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react"
import { useRouter, usePathname } from "next/navigation"

import { getApiBaseUrl, authHeaders, publicHeaders } from "@/lib/api"
import { MaintenanceScreen } from "@/components/auth/maintenance-screen"

function getAuthApiBaseUrl(): string {
  return `${getApiBaseUrl()}/api`
}

interface User {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  is_staff: boolean
  is_superuser: boolean
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  getToken: () => string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const TOKEN_REFRESH_INTERVAL_MS = 45 * 60 * 1000
// Maintenance gate. Flip MAINTENANCE_MODE to true to lock everyone
// out except the whitelist (used during big rebuilds).
const MAINTENANCE_MODE = false
const MAINTENANCE_ALLOWED_USERS = new Set(["rodrigo"])

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()
  const pathname = usePathname()
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null)

  const API_BASE_URL = getAuthApiBaseUrl()
  const isAuthenticated = user !== null
  const isMaintenanceAllowed = Boolean(user?.username && MAINTENANCE_ALLOWED_USERS.has(user.username))
  const isLoginRoute = pathname === "/auth/login"
  const shouldShowMaintenance = MAINTENANCE_MODE && !isMaintenanceAllowed && !isLoginRoute

  const getToken = () => {
    if (typeof window === "undefined") return null
    return localStorage.getItem("access_token")
  }

  const refreshAccessToken = useCallback(async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem("refresh_token")
    if (!refreshToken) return false

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh/`, {
        method: "POST",
        headers: publicHeaders(),
        body: JSON.stringify({ refresh: refreshToken }),
      })

      if (response.ok) {
        const data = await response.json()
        localStorage.setItem("access_token", data.access)
        if (data.refresh) {
          localStorage.setItem("refresh_token", data.refresh)
        }
        return true
      }
    } catch {
      // refresh failed silently
    }

    return false
  }, [API_BASE_URL])

  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current)
    refreshTimerRef.current = setInterval(async () => {
      const success = await refreshAccessToken()
      if (!success) {
        localStorage.removeItem("access_token")
        localStorage.removeItem("refresh_token")
        setUser(null)
        router.push("/auth/login")
      }
    }, TOKEN_REFRESH_INTERVAL_MS)
  }, [refreshAccessToken, router])

  const stopRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
  }, [])

  const checkAuth = async () => {
    const token = getToken()

    if (!token) {
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/me/`, {
        headers: authHeaders(token),
      })

      if (response.ok) {
        const userData = await response.json()
        setUser(userData)
        startRefreshTimer()
      } else if (response.status === 401) {
        const refreshed = await refreshAccessToken()
        if (refreshed) {
          const retryResponse = await fetch(`${API_BASE_URL}/auth/me/`, {
            headers: authHeaders(getToken()),
          })
          if (retryResponse.ok) {
            const userData = await retryResponse.json()
            setUser(userData)
            startRefreshTimer()
            return
          }
        }
        localStorage.removeItem("access_token")
        localStorage.removeItem("refresh_token")
        setUser(null)
      } else {
        localStorage.removeItem("access_token")
        localStorage.removeItem("refresh_token")
        setUser(null)
      }
    } catch (error) {
      console.error("Error checking auth:", error)
      localStorage.removeItem("access_token")
      localStorage.removeItem("refresh_token")
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (username: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/auth/login/`, {
      method: "POST",
      headers: publicHeaders(),
      body: JSON.stringify({ username, password }),
    })

    if (!response.ok) {
      let errorMessage = "Credenciais inválidas"
      try {
        const contentType = response.headers.get("content-type")
        if (contentType?.includes("application/json")) {
          const errorData = await response.json()
          if (response.status === 403) {
            errorMessage = errorData.error || "Acesso negado a este painel"
          } else {
            errorMessage =
              errorData.error || errorData.detail || errorData.non_field_errors?.[0] || errorMessage
          }
        } else if (response.status === 401) {
          errorMessage = "Usuário ou senha incorretos"
        } else if (response.status >= 500) {
          errorMessage = "Erro no servidor. Tente novamente mais tarde."
        }
      } catch {
        if (response.status === 401) errorMessage = "Usuário ou senha incorretos"
        if (response.status === 403) errorMessage = "Acesso negado a este painel"
      }
      throw new Error(errorMessage)
    }

    const data = await response.json()

    localStorage.setItem("access_token", data.access)
    localStorage.setItem("refresh_token", data.refresh)

    setUser(data.user)
    startRefreshTimer()

    router.push("/dashboard-cms")
  }

  const logout = async () => {
    stopRefreshTimer()
    const refreshToken = localStorage.getItem("refresh_token")

    try {
      if (refreshToken) {
        await fetch(`${API_BASE_URL}/auth/logout/`, {
          method: "POST",
          headers: authHeaders(getToken()),
          body: JSON.stringify({ refresh: refreshToken }),
        })
      }
    } catch (error) {
      console.error("Logout error:", error)
    } finally {
      localStorage.removeItem("access_token")
      localStorage.removeItem("refresh_token")
      localStorage.removeItem("promobot_tenant_config")
      setUser(null)
      router.push("/auth/login")
    }
  }

  useEffect(() => {
    checkAuth()
    return () => stopRefreshTimer()
  }, [])

  useEffect(() => {
    if (MAINTENANCE_MODE && !isMaintenanceAllowed) return

    if (!isLoading && !isAuthenticated && pathname && !pathname.startsWith("/auth")) {
      router.push("/auth/login")
    }
  }, [isLoading, isAuthenticated, pathname, router, isMaintenanceAllowed])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        getToken,
      }}
    >
      {shouldShowMaintenance ? (
        <MaintenanceScreen username={user?.username} onLogout={isAuthenticated ? logout : undefined} />
      ) : (
        children
      )}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
